import {
  DEFAULT_LIMIT,
  displayName,
  fetchPlayerTurnovers,
  formatBdt,
  formatTurnoverDate,
} from './helpers.js';

export function createTurnoverPage({ useState, useEffect, useLayoutEffect, useCallback, jsx, jsxs, Fragment }) {
  const F = Fragment || 'div';

  function TurnoverCard({ row, lang, t, emphasizeRemaining }) {
    const pct = Math.min(100, Math.max(0, Number(row.progressPercent) || 0));
    const status = row.status === 'COMPLETED' ? 'COMPLETED' : 'ACTIVE';
    return jsxs('article', {
      className: `to-card to-card-${status.toLowerCase()}`,
      'data-turnover-id': row.id,
      children: [
        jsx('h3', { className: 'to-card-title', children: displayName(row) }),
        jsx('p', { className: 'to-card-source', children: row.sourceLabel }),
        jsxs('dl', {
          className: 'to-card-stats',
          children: [
            jsxs('div', {
              children: [
                jsx('dt', { children: t('toRequired') }),
                jsx('dd', { children: formatBdt(row.requiredTurnover, lang) }),
              ],
            }),
            jsxs('div', {
              children: [
                jsx('dt', { children: t('toCompletedTurnover') }),
                jsx('dd', { children: formatBdt(row.completedTurnover, lang) }),
              ],
            }),
            jsxs('div', {
              className: emphasizeRemaining ? 'to-remaining' : '',
              children: [
                jsx('dt', { children: t('toRemaining') }),
                jsx('dd', { children: formatBdt(row.remainingTurnover, lang) }),
              ],
            }),
          ],
        }),
        jsxs('div', {
          className: 'to-progress',
          'aria-label': `${pct}%`,
          children: [
            jsx('div', {
              className: 'to-progress-track',
              children: jsx('div', { className: 'to-progress-fill', style: { width: `${pct}%` } }),
            }),
            jsx('span', { className: 'to-progress-pct', children: `${pct}%` }),
          ],
        }),
        jsx('p', {
          className: `to-status to-status-${status.toLowerCase()}`,
          children: status === 'COMPLETED' ? t('toCompleted') : t('toActive'),
        }),
        jsxs('p', {
          className: 'to-meta',
          children: [t('toStarted'), ': ', formatTurnoverDate(row.createdAt, lang)],
        }),
        status === 'COMPLETED'
          ? jsxs('p', {
            className: 'to-meta',
            children: [t('toCompletedAt'), ': ', formatTurnoverDate(row.completedAt, lang)],
          })
          : null,
      ],
    });
  }

  function TurnoverColumn({
    title,
    empty,
    rows,
    loading,
    error,
    hasMore,
    onMore,
    lang,
    t,
    emphasizeRemaining,
  }) {
    return jsxs('section', {
      className: 'to-col',
      children: [
        jsx('h2', { className: 'to-col-title', children: title }),
        loading && !rows.length ? jsx('div', { className: 'account-skel-line to-skel', 'aria-hidden': 'true' }) : null,
        loading && !rows.length ? jsx('p', { className: 'to-muted', children: t('toLoading') }) : null,
        error ? jsx('p', { className: 'to-error', children: error }) : null,
        !loading && !error && !rows.length ? jsx('p', { className: 'to-empty', children: empty }) : null,
        jsx('div', {
          className: 'to-list',
          children: rows.map((row) => jsx(TurnoverCard, {
            key: row.id,
            row,
            lang,
            t,
            emphasizeRemaining,
          })),
        }),
        hasMore
          ? jsx('button', {
            type: 'button',
            className: 'to-more',
            onClick: onMore,
            disabled: loading,
            children: t('toLoadMore'),
          })
          : null,
      ],
    });
  }

  function useTurnoverList(status) {
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async (nextPage, append) => {
      setLoading(true);
      setError('');
      try {
        const payload = await fetchPlayerTurnovers({ status, page: nextPage, limit: DEFAULT_LIMIT });
        const list = Array.isArray(payload.data) ? payload.data : [];
        setRows((prev) => (append ? [...prev, ...list] : list));
        setPage(payload.page || nextPage);
        setTotalPages(payload.totalPages || 0);
      } catch (err) {
        if (!append) setRows([]);
        setError(err.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    }, [status]);

    useEffect(() => {
      load(1, false);
    }, [load]);

    return {
      rows,
      loading,
      error,
      hasMore: page < totalPages,
      loadMore: () => load(page + 1, true),
    };
  }

  function TurnoverPage({ t, lang = 'en', onBack }) {
    const [mobileTab, setMobileTab] = useState('ACTIVE');
    const active = useTurnoverList('ACTIVE');
    const completed = useTurnoverList('COMPLETED');
    const goBack = () => {
      if (typeof onBack === 'function') onBack();
    };
    const label = (key) => (typeof t === 'function' ? t(key) : key);

    const activeCol = jsx(TurnoverColumn, {
      title: label('toActiveHeading'),
      empty: label('toNoActive'),
      rows: active.rows,
      loading: active.loading,
      error: active.error,
      hasMore: active.hasMore,
      onMore: active.loadMore,
      lang,
      t: label,
      emphasizeRemaining: true,
    });
    const completedCol = jsx(TurnoverColumn, {
      title: label('toCompletedHeading'),
      empty: label('toNoCompleted'),
      rows: completed.rows,
      loading: completed.loading,
      error: completed.error,
      hasMore: completed.hasMore,
      onMore: completed.loadMore,
      lang,
      t: label,
      emphasizeRemaining: false,
    });

    return jsx('div', {
      className: 'turnover-page',
      'aria-label': label('profTurnover'),
      children: jsxs('div', {
        className: 'to-wrap',
        children: [
          jsxs('div', {
            className: 'to-head',
            children: [
              jsx('button', {
                type: 'button',
                className: 'to-back',
                onClick: goBack,
                children: `‹ ${label('toBack')}`,
              }),
              jsx('h1', { className: 'to-title', children: label('profTurnover') }),
            ],
          }),
          jsxs('div', {
            className: 'to-mobile-tabs',
            role: 'tablist',
            children: [
              jsx('button', {
                type: 'button',
                role: 'tab',
                'aria-selected': mobileTab === 'ACTIVE',
                className: `to-tab${mobileTab === 'ACTIVE' ? ' to-tab-active' : ''}`,
                onClick: () => setMobileTab('ACTIVE'),
                children: label('toActive'),
              }),
              jsx('button', {
                type: 'button',
                role: 'tab',
                'aria-selected': mobileTab === 'COMPLETED',
                className: `to-tab${mobileTab === 'COMPLETED' ? ' to-tab-active' : ''}`,
                onClick: () => setMobileTab('COMPLETED'),
                children: label('toCompleted'),
              }),
            ],
          }),
          jsxs('div', {
            className: 'to-desktop',
            children: [activeCol, completedCol],
          }),
          jsx('div', {
            className: 'to-mobile-panels',
            children: mobileTab === 'ACTIVE' ? activeCol : completedCol,
          }),
        ],
      }),
    });
  }

  return TurnoverPage;
}
